"use client";

import { cn } from "@/lib/tailwind";
import { mergeRefs } from "@/lib/mergeRefs";
import React, { MouseEventHandler, useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { useModal } from "@/components/ui/modal_new/hooks/useModal";
import { useFocusTrap } from "@/components/ui/modal_new/hooks/useFocusTrap";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: React.ReactNode;
  children: React.ReactNode;
  headerClassName?: string;
}

const Modal: React.FC<ModalProps> = ({ isOpen, onClose, title, children, headerClassName }) => {
  const modalId = useId();
  const focusTrapRef = useFocusTrap(isOpen);
  const { modalRef } = useModal({ isOpen, onClose });
  const closingRef = useRef(false);

  useEffect(() => {
    if (isOpen) closingRef.current = false;
  }, [isOpen]);

  const handleOnClose: MouseEventHandler<HTMLButtonElement | HTMLDivElement> = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (closingRef.current) return;
    closingRef.current = true;
    onClose();
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={modalId}
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
    >
      <div
        aria-hidden="true"
        onClick={handleOnClose}
        className="absolute inset-0 bg-black/60"
      />

      <div
        tabIndex={-1}
        ref={mergeRefs(modalRef, focusTrapRef)}
        className="relative m-4 max-h-screen w-max min-w-96 max-w-screen-xl bg-white shadow-lg focus:outline-none"
      >
        {/* Modal Header */}
        <div
          className={cn(
            "flex h-14 items-center justify-between bg-primary-4 px-6",
            headerClassName,
          )}
        >
          {typeof title === "string" ? (
            <h2 id={modalId} className="text-md truncate font-medium">{title}</h2>
          ) : (
            <div id={modalId}>{title}</div>
          )}

          <button
            onClick={handleOnClose}
            className="text-gray-600 hover:text-gray-800"
            aria-label="Close"
          >
            <svg
              className="h-6 w-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Modal Content */}
        <div className="max-h-[calc(100vh-8rem)] overflow-y-auto p-6">{children}</div>
      </div>
    </div>,
    document.body,
  );
};

export default Modal;
